import * as React from "react";
import { useCallback } from "react";
import { Grid2 as Grid } from "@mui/material";
import { CompanySignUpDTO, Country } from "@/api/data-contracts";
import { ChangeHandler } from "@/lib/modelstate";
import { ErrorsShape, extractEvent } from "@/lib/utils";
import { CardEx } from "@/lib/components/CardEx";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import SelectInputField from "@/lib/components/inputfields/select-input-field";
import { countryOptions, useLegalFormsByCountry } from "@/datacaches";
import { useTranslation } from "react-i18next";
import { CommonI18N, CompanyI18N } from "@/translations";
import { Constants } from "@/api/constants";
import { allowedLegalForms, isLegalFormAllowed } from "@/model/legalForm";

export const CompanyForm = ({
    model,
    errors,
    onChange
}: {
    model?: CompanySignUpDTO;
    onChange: ChangeHandler;
    errors: ErrorsShape<CompanySignUpDTO>;
}) => {
    const { t } = useTranslation();
    const [legalFormsByCountry] = useLegalFormsByCountry();
    const countrySelectOptions = countryOptions.map((option) => ({
        label: option && t(CommonI18N.enum.country[option]),
        value: option
    }));
    const isLegalFormDisabled = !model?.country;
    const businessStructureSelectOptions = allowedLegalForms(model?.country, legalFormsByCountry).map((option) => ({
        label: t(CommonI18N.enum.legalForm[option]),
        value: option
    }));

    /**
     * Wraps the shared onChange handler for the country field: applies the country change first (so the
     * new country is always set), then — using the legal form value still held in this closure, i.e. the
     * value BEFORE the change — clears the legal form if it is no longer valid for the new country (AK7).
     * A legal form that remains valid for the new country (including the cross-country SE/Sonstige
     * fallback) is left untouched (AK8).
     */
    const handleCountryChange: ChangeHandler = useCallback(
        (e) => {
            onChange(e);
            const nv = extractEvent(e);
            if (nv?.[0] === "country") {
                const newCountry = nv[1] as Country;
                if (model?.legalForm && !isLegalFormAllowed(newCountry, model.legalForm, legalFormsByCountry)) {
                    onChange({ name: "legalForm", value: "" });
                }
            }
        },
        [onChange, model?.legalForm, legalFormsByCountry]
    );

    return (
        <CardEx title={t(CompanyI18N.form.page.title)}>
            <Grid container direction="column" spacing={3} width="100%">
                <Grid size={{ sm: 6, md: 5, lg: 4 }}>
                    <StringInputField
                        name="companyName"
                        value={model?.companyName}
                        label={t(CompanyI18N.form.field.name)}
                        error={errors?.companyName}
                        maxChars={Constants.COMPANY_NAME_LENGTH_MAX}
                        onChange={onChange}
                    />
                </Grid>
                <Grid container direction="row" gap={3} width="100%">
                    <Grid size={{ sm: 4, md: 3, lg: 2 }}>
                        <SelectInputField
                            name="country"
                            label={t(CompanyI18N.form.field.country)}
                            value={model?.country}
                            error={errors?.country}
                            options={countrySelectOptions}
                            displayEmpty={true}
                            onChange={handleCountryChange}
                        />
                    </Grid>
                    <Grid size={{ sm: 4, md: 3, lg: 2 }}>
                        <SelectInputField
                            name="legalForm"
                            label={t(CompanyI18N.form.field.legalForm)}
                            value={model?.legalForm}
                            // SelectInputField prefers error.message over helperText — while the field is
                            // disabled (no country chosen yet), the required-legalForm error must not win over
                            // the "choose a country first" hint (AK1), so the error is suppressed here.
                            error={isLegalFormDisabled ? undefined : errors?.legalForm}
                            options={businessStructureSelectOptions}
                            displayEmpty={true}
                            disabled={isLegalFormDisabled}
                            helperText={
                                isLegalFormDisabled ? t(CompanyI18N.form.field.legalFormCountryHint) : undefined
                            }
                            onChange={onChange}
                        />
                    </Grid>
                </Grid>

                <Grid size={{ sm: 12, md: 6, lg: 4 }}>
                    <StringInputField
                        name="website"
                        value={model?.website}
                        label={t(CompanyI18N.form.field.website)}
                        error={errors?.website}
                        maxChars={Constants.WEBSITE_LENGTH_MAX}
                        onChange={onChange}
                        activatePrefix={true}
                        prefixValue="https://"
                    />
                </Grid>
                <Grid container direction="row" gap={3} width="100%">
                    <Grid size={{ sm: 8, md: 4, lg: 2 }}>
                        <StringInputField
                            name="commercialRegisterNumber"
                            value={model?.commercialRegisterNumber}
                            label={t(CompanyI18N.form.field.commercialRegisterNumber)}
                            error={errors?.commercialRegisterNumber}
                            maxChars={Constants.COMMERCIAL_REGISTER_NUMBER_LENGTH_MAX}
                            onChange={onChange}
                        />
                    </Grid>
                    <Grid size={{ sm: 4, md: 3, lg: 2 }}>
                        <StringInputField
                            name="vatId"
                            value={model?.vatId}
                            label={t(CompanyI18N.form.field.vatId)}
                            error={errors?.vatId}
                            maxChars={Constants.VAT_ID_LENGTH_MAX}
                            onChange={onChange}
                        />
                    </Grid>
                </Grid>

                <Grid container direction="row" gap={3} width="100%">
                    <Grid size={{ sm: 8, md: 4, lg: 3 }}>
                        <StringInputField
                            name="streetAddress"
                            value={model?.streetAddress}
                            label={t(CompanyI18N.form.field.street)}
                            error={errors?.streetAddress}
                            maxChars={Constants.STREET_LENGTH_MAX}
                            onChange={onChange}
                        />
                    </Grid>
                    <Grid size={{ sm: 4, md: 2, lg: 1 }}>
                        <StringInputField
                            name="houseNumber"
                            value={model?.houseNumber}
                            label={t(CompanyI18N.form.field.houseNumber)}
                            error={errors?.houseNumber}
                            maxChars={Constants.HOUSE_NUMBER_LENGTH_MAX}
                            onChange={onChange}
                        />
                    </Grid>
                </Grid>
                <Grid container direction="row" gap={3} width="100%">
                    <Grid size={{ sm: 4, md: 2, lg: 2 }}>
                        <StringInputField
                            name="postalCode"
                            value={model?.postalCode}
                            label={t(CompanyI18N.form.field.postalCode)}
                            error={errors?.postalCode}
                            maxChars={Constants.ZIP_LENGTH_MAX}
                            onChange={onChange}
                        />
                    </Grid>
                    <Grid size={{ sm: 4, md: 2, lg: 2 }}>
                        <StringInputField
                            name="city"
                            value={model?.city}
                            label={t(CompanyI18N.form.field.city)}
                            error={errors?.city}
                            maxChars={Constants.CITY_LENGTH_MAX}
                            onChange={onChange}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </CardEx>
    );
};
